<template>
  <header class="topbar">
    <div class="topbar-left">
      <RouterLink to="/" class="brand">
        <svg
          class="brand-mark"
          viewBox="0 0 100 100"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <circle cx="50" cy="50" r="34" fill="none" stroke="currentColor" stroke-width="3" />
          <g stroke="currentColor" stroke-width="8" stroke-linecap="square" fill="none">
            <line x1="40" y1="32" x2="40" y2="68" />
            <line x1="40" y1="32" x2="60" y2="32" />
            <line x1="40" y1="49" x2="55" y2="49" />
          </g>
          <circle cx="78.85" cy="32.97" r="6" fill="currentColor" />
        </svg>
        <span>Sprint Scorecard</span>
      </RouterLink>
      <nav class="crumbs" aria-label="Breadcrumb">
        <template v-for="(c, i) in crumbs" :key="i">
          <span v-if="i > 0" class="sep" aria-hidden="true">›</span>
          <RouterLink v-if="c.to" :to="c.to" class="crumb-link">{{ c.label }}</RouterLink>
          <span v-else class="here">{{ c.label }}</span>
        </template>
      </nav>
    </div>

    <div class="topbar-right">
      <RouterLink
        v-if="auth.isSignedIn && auth.isAdmin"
        :to="{ name: 'admin' }"
        class="admin-link"
        :class="{ active: $route.name === 'admin' }"
        data-test="topbar-admin-link"
      >Admin</RouterLink>
      <span v-if="auth.isSignedIn" class="email">{{ auth.email }}</span>
      <button
        v-if="auth.isSignedIn"
        type="button"
        class="signout"
        @click="auth.signOut"
      >
        Sign out
      </button>
    </div>
  </header>

  <main>
    <RouterView />
  </main>

  <footer class="credit-bar" role="contentinfo">
    <span>Built with</span>
    <span class="heart" aria-hidden="true">♥</span>
    <span>by the AI Foodics team</span>
    <span class="sep" aria-hidden="true">·</span>
    <span>
      Questions? Ping
      <a
        href="https://slack.com/app_redirect?channel=amine-aitelharraj"
        target="_blank"
        rel="noopener noreferrer"
      >Amine Ait El Harraj</a>
      on Slack
    </span>
  </footer>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { RouterLink, RouterView, useRoute } from "vue-router";
import { useAuthStore } from "@/stores/auth";

const auth = useAuthStore();
const route = useRoute();

interface Crumb { label: string; to?: string }

const crumbs = computed<Crumb[]>(() => {
  const name = route.name;
  if (name === "project") {
    const key = String(route.params.key ?? "");
    return [
      { label: "Triage", to: "/" },
      { label: key },
    ];
  }
  if (name === "sprint") {
    const key = String(route.params.key ?? "");
    return [
      { label: "Triage", to: "/" },
      { label: key, to: `/p/${key}` },
      { label: "Sprint" },
    ];
  }
  if (name === "admin") {
    return [{ label: "Admin · users" }];
  }
  return [{ label: "Project triage" }];
});
</script>

<style scoped>
.topbar {
  position: sticky; top: 0; z-index: 50;
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 32px;
  background: color-mix(in srgb, var(--bg) 85%, transparent);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  border-bottom: 1px solid var(--line);
}
.topbar-left {
  display: flex; align-items: center;
  min-width: 0;
}
.brand {
  display: flex; align-items: center; gap: 10px;
  font-weight: 600; letter-spacing: -0.01em;
  font-size: 15px;
  color: var(--ink);
}
.brand:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--brand) 22%, transparent);
  border-radius: 6px;
}
.brand-mark {
  width: 22px;
  height: 22px;
  color: var(--brand);
  display: block;
  flex-shrink: 0;
  transition: transform var(--speed-quick) var(--ease);
}
.brand:hover .brand-mark {
  transform: rotate(8deg);
}
.crumbs {
  display: flex; align-items: center; gap: 10px;
  font-size: 13px; color: var(--muted);
  margin-left: 24px;
  min-width: 0;
}
.crumbs .sep { opacity: 0.4; }
.crumb-link {
  cursor: pointer;
  transition: color var(--speed-quick) var(--ease);
}
.crumb-link:hover { color: var(--ink); }
.crumb-link:focus-visible {
  outline: none; color: var(--brand);
}
.crumbs .here { color: var(--ink); font-weight: 500; }

.topbar-right {
  display: flex; align-items: center; gap: 14px;
  font-size: 13px; color: var(--muted);
}
.admin-link {
  font-family: "JetBrains Mono", monospace;
  font-size: 11px; letter-spacing: 0.14em; text-transform: uppercase;
  color: var(--brand);
  padding: 6px 12px;
  border: 1px solid color-mix(in srgb, var(--brand) 30%, transparent);
  border-radius: 999px;
  transition: background var(--speed-quick) var(--ease),
              border-color var(--speed-quick) var(--ease),
              color var(--speed-quick) var(--ease);
}
.admin-link:hover {
  background: var(--brand-soft);
  border-color: var(--brand);
}
.admin-link.active {
  background: var(--brand);
  color: #FFF;
  border-color: var(--brand);
}
.admin-link:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--brand) 22%, transparent);
}
.email {
  white-space: nowrap;
  text-overflow: ellipsis; overflow: hidden;
  max-width: 240px;
}
.signout {
  border: 1px solid var(--line);
  background: var(--bg-elev);
  padding: 7px 14px;
  border-radius: 8px;
  font-size: 13px; color: var(--ink-2);
  cursor: pointer;
  transition: border-color var(--speed-quick) var(--ease),
              transform var(--speed-quick) var(--ease);
}
.signout:hover { border-color: var(--ink); transform: translateY(-1px); }
.signout:focus-visible {
  outline: none;
  border-color: var(--brand);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--brand) 18%, transparent);
}

main {
  min-height: calc(100vh - 56px);
}
</style>
